import { useCallback, useState } from 'react';
import {
  type AdminConfigDraft,
  applyConfigDraft,
  listConfigDrafts,
  rejectConfigDraft,
} from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';
import { type Notify, Panel, btn, btnGhost } from './ui.js';

/** Plain-English summary of a rate-draft payload, so an operator can review at a glance. */
function summarize(payload: unknown, productLine: string | null): string {
  const cfg = (payload ?? {}) as Record<string, unknown>;
  if (productLine === 'major_medical' && Array.isArray(cfg.rates)) {
    return (cfg.rates as { planCode: string | null; rateType: string; rateValue: number }[])
      .map((r) => {
        const who = r.planCode ?? 'default';
        const val =
          r.rateType === 'percent_of_premium'
            ? `${(r.rateValue * 100).toFixed(2)}% of premium`
            : `$${r.rateValue} per member`;
        return `${who}: ${val}`;
      })
      .join(' · ');
  }
  if (productLine === 'medicare' && Array.isArray(cfg.cmsTable)) {
    return `${(cfg.cmsTable as unknown[]).length} CMS rate row(s)`;
  }
  if (productLine === 'ancillary' && Array.isArray(cfg.yearRates)) {
    return `${(cfg.yearRates as unknown[]).length} policy-year band(s)`;
  }
  return 'Rate schedule ready for review.';
}

function DraftRow({
  draft,
  onApply,
  onReject,
}: {
  draft: AdminConfigDraft;
  onApply: () => void;
  onReject: () => void;
}) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-neutral-200 p-2 text-sm">
      <div className="min-w-0">
        <div className="font-medium">
          {draft.accountName} · {draft.carrierName ?? 'all carriers'} · {draft.productLine}
        </div>
        <div className="mt-0.5 text-neutral-600">{summarize(draft.payload, draft.productLine)}</div>
        <button
          type="button"
          onClick={() => setShowRaw((s) => !s)}
          className="mt-1 text-xs text-neutral-400 hover:underline"
        >
          {showRaw ? 'Hide details' : 'View details'}
        </button>
        {showRaw && (
          <pre className="mt-1 max-h-32 overflow-auto rounded bg-neutral-50 p-1 text-[11px] text-neutral-600">
            {JSON.stringify(draft.payload, null, 1)}
          </pre>
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        <button type="button" className={btn} onClick={onApply}>
          Apply
        </button>
        <button type="button" className={btnGhost} onClick={onReject}>
          Reject
        </button>
      </div>
    </div>
  );
}

/** Back-office review of AI-generated rate drafts: apply to the account's live
 * config, or reject. Renders nothing when the queue is empty. */
export function RateDraftReview({ notify }: { notify: Notify }) {
  const { data, reload } = useAsyncData(
    useCallback(() => listConfigDrafts().then((r) => r.drafts), []),
  );
  const drafts = data ?? [];
  if (drafts.length === 0) return null;

  const act = async (p: Promise<unknown>, ok: string) => {
    try {
      await p;
      notify(ok, 'success');
      reload();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Action failed', 'error');
    }
  };

  return (
    <div className="mb-5">
      <Panel
        title={`Pending AI rate drafts (${drafts.length}) — applying writes to a live account`}
      >
        <div className="space-y-2">
          {drafts.map((d) => (
            <DraftRow
              key={d.id}
              draft={d}
              onApply={() => act(applyConfigDraft(d.id), 'Draft applied')}
              onReject={() => act(rejectConfigDraft(d.id), 'Draft rejected')}
            />
          ))}
        </div>
      </Panel>
    </div>
  );
}
